import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  MagnifyingGlass,
  PlusCircle,
  EnvelopeSimple,
  Buildings,
  MapPin,
  IdentificationBadge,
  Funnel,
  Check,
  PencilSimple,
  TrashSimple,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Loader2 } from "lucide-react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {
  getAllusers,
  useGetUserRoles,
  useVacancyDetails,
} from "../../../adapters/Requests";
import { useFlashMessageContext } from "../../../contexts/FlashMessageContext";
import ExportDropdown from "../../export";
import {
  Dialog as DialogX,
  DialogContent as DialogContentX,
  DialogHeader,
  DialogTitle as DialogTitleX,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
export default function UsersTable() {
  const apiClientPrivate = useAxiosPrivate();
  const { data, isLoading, isError, refetch } = getAllusers();
  const { data: userRoles } = useGetUserRoles();
  const businessDetails = useVacancyDetails();

  const [globalFilter, setGlobalFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [sorting, setSorting] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [userDialog, setUserDialog] = useState({
    open: false,
    mode: "add",
    data: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
    loading: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    staffid: "",
    firstname: "",
    lastname: "",
    email: "",
    role: "",
    location: "",
    sbu: "",
  });

  const { setOpenFlash, setSeverity, setFlashMessage } =
    useFlashMessageContext();

  useEffect(() => {
    if (data) {
      const formattedData = data.map((user) => {
        const roleRaw = user.staffRoles?.[0]?.role?.name || "";
        return {
          id: user.id,
          staffid: user.staff_id,
          email: user.email,
          fullname: `${user.first_name} ${user.last_name}`,
          firstname: user.first_name,
          lastname: user.last_name,
          sbu: user.business_unit?.name || "",
          role: roleRaw
            ? roleRaw.charAt(0).toUpperCase() + roleRaw.slice(1)
            : "N/A",
          rawRole: roleRaw,
          location: user.location?.name || "",
          original: user,
        };
      });
      setTableData(formattedData);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    return tableData.filter((item) => {
      const matchesSearch =
        globalFilter === "" ||
        Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(globalFilter.toLowerCase()),
        );
      const matchesRole = roleFilter === "ALL" || item.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [tableData, globalFilter, roleFilter]);

  const uniqueRoles = useMemo(
    () => [...new Set(tableData.map((d) => d.role))].filter(Boolean).sort(),
    [tableData],
  );

  const roleOptions = useMemo(() => {
    if (!Array.isArray(userRoles)) return [];

    return userRoles
      .map((role) => role?.name)
      .filter(Boolean)
      .map((name) => name.toLowerCase())
      .sort((a, b) => a.localeCompare(b));
  }, [userRoles]);

  const exportColumns = [
    { headerName: "Staff ID", field: "staffid" },
    { headerName: "Full Name", field: "fullname" },
    { headerName: "Email", field: "email" },
    { headerName: "Role", field: "role" },
    { headerName: "Business Unit", field: "sbu" },
    { headerName: "Location", field: "location" },
  ];

  const handleOpenDialog = (mode, userData = null) => {
    if (mode === "edit" && userData) {
      setFormData({
        staffid: userData.staffid,
        firstname: userData.firstname,
        lastname: userData.lastname,
        email: userData.email,
        role: userData.rawRole,
        location: userData.location,
        sbu: userData.sbu,
        id: userData.id,
      });
    } else {
      setFormData({
        staffid: "",
        firstname: "",
        lastname: "",
        email: "",
        role: "",
        location: "",
        sbu: "",
      });
    }
    setUserDialog({ open: true, mode, data: userData });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.staffid || !formData.email || !formData.role) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        staffid: formData.staffid,
        first_name: formData.firstname,
        last_name: formData.lastname,
        email: formData.email,
        role: formData.role.toLowerCase(),
        location: formData.location,
        sbu: formData.sbu,
      };

      const url =
        userDialog.mode === "add"
          ? `/user/add/${formData.staffid}`
          : `/user/add/${formData.id}`;

      const res = await apiClientPrivate.put(url, payload);

      if (res.status === 200) {
        setFlashMessage(
          `User ${userDialog.mode === "add" ? "added" : "updated"} successfully`,
        );
        setSeverity("success");
        setOpenFlash(true);
        refetch();
        setUserDialog({ open: false, mode: "add", data: null });
      }
    } catch (error) {
      console.error(error);
      toast.error("Operation failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.id) return;
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));
    try {
      const res = await apiClientPrivate.put(
        `/user/delete/${deleteConfirm.id}`,
        {
          deleted_at: new Date().toISOString(),
        },
      );
      if (res.status === 200) {
        setTableData((prev) =>
          prev.filter((u) => u.staffid !== deleteConfirm.id),
        );
        toast.success("User deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleteConfirm({ open: false, id: null, loading: false });
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "outline";
      case "management":
        return "outline";
      case "supervisor":
        return "outline";
      case "head":
        return "outline";
      case "approver":
        return "outline";
      case "verifier":
        return "outline";
      case "viewer":
        return "outline";
      default:
        return "outline";
    }
  };

  const columns = [
    {
      accessorKey: "fullname",
      header: "User Details",
      cell: ({ row }) => (
        <div className="flex flex-col py-1">
          <span className="font-bold text-gray-900 text-sm">
            {row.getValue("fullname")}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <EnvelopeSimple size={12} className="text-gray-400" />
            <span className="text-[11px] text-gray-500">
              {row.original.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "staffid",
      header: "Staff ID",
      cell: ({ row }) => (
        <div className="flex items-center text-gray-600 font-mono text-xs">
          <IdentificationBadge className="mr-1.5 text-green-600" size={16} />
          {row.getValue("staffid")}
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => (
        <Badge
          variant={getRoleColor(row.getValue("role"))}
          className="font-semibold text-[10px] px-2 uppercase tracking-wide"
        >
          {row.getValue("role")}
        </Badge>
      ),
    },
    {
      accessorKey: "sbu",
      header: "Business Unit",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-gray-700 text-xs font-medium">
            <Buildings className="mr-1.5 text-gray-400" size={14} />
            {row.getValue("sbu")}
          </div>
          <div className="flex items-center text-gray-500 text-[11px]">
            <MapPin className="mr-1.5 text-gray-400" size={14} />
            {row.original.location}
          </div>
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleOpenDialog("edit", row.original)}
            className="h-8 w-8 p-0 text-gray-500 hover:text-green-600 hover:bg-green-50"
            title="Edit User"
          >
            <PencilSimple size={18} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setDeleteConfirm({ open: true, id: row.original.staffid })
            }
            className="h-8 w-8 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
            title="Delete User"
          >
            <TrashSimple size={18} />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { sorting },
    onSortingChange: setSorting,
  });

  if (isLoading) {
    return (
      <Card className="w-full h-96 flex items-center justify-center border-slate-200">
        <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
      </Card>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center text-red-500">Error loading users.</div>
    );
  }

  return (
    <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white">
      <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-dashed border-gray-300"
                >
                  <Funnel className="mr-2 h-3 w-3" />
                  Role: {roleFilter === "ALL" ? "All" : roleFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={() => setRoleFilter("ALL")}>
                  {roleFilter === "ALL" && <Check className="mr-2 h-3 w-3" />}{" "}
                  All Roles
                </DropdownMenuItem>
                {uniqueRoles.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => setRoleFilter(role)}
                  >
                    {roleFilter === role && <Check className="mr-2 h-3 w-3" />}{" "}
                    {role}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={14}
              />
              <Input
                placeholder="Search users..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
              />
            </div>

            <Button
              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 text-xs shadow-sm"
              onClick={() => handleOpenDialog("add")}
            >
              <PlusCircle size={16} className="mr-1.5" weight="bold" />
              Add User
            </Button>

            <ExportDropdown
              onExport={() => { }}
              data={filteredData}
              columns={exportColumns}
              className="h-8"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <Table>
          <TableHeader className="bg-gray-50/50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-transparent border-b border-slate-100"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-9 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-green-50/30 transition-colors border-b border-slate-100 group"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-32 text-center text-gray-500"
                >
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
        <div className="text-[11px] text-gray-500">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            <CaretLeft className="mr-1" size={12} /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            Next <CaretRight className="ml-1" size={12} />
          </Button>
        </div>
      </div>

      <DialogX
        open={userDialog.open}
        onOpenChange={(open) =>
          !open && setUserDialog((prev) => ({ ...prev, open: false }))
        }
      >
        <DialogContentX className="sm:max-w-[600px] z-[100]">
          <DialogHeader>
            <DialogTitleX>
              {userDialog.mode === "add" ? "Add New User" : "Edit User Details"}
            </DialogTitleX>
            <DialogDescription>
              {userDialog.mode === "add"
                ? "Create a new user account with specific permissions."
                : "Update user information and permissions."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <span className="text-xs font-medium">First Name</span>
                <Input
                  value={formData.firstname}
                  onChange={(e) =>
                    handleFormChange("firstname", e.target.value)
                  }
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium">Last Name</span>
                <Input
                  value={formData.lastname}
                  onChange={(e) => handleFormChange("lastname", e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <span className="text-xs font-medium">Staff ID</span>
                <Input
                  value={formData.staffid}
                  onChange={(e) => handleFormChange("staffid", e.target.value)}
                  placeholder="e.g. 123456"
                  disabled={userDialog.mode === "edit"}
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium">Email</span>
                <Input
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  placeholder="john.doe@nnpc.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <span className="text-xs font-medium">Role</span>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleFormChange("role", value)}
                  className="z-50"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((role) => (
                      <SelectItem value={role} key={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium">Location</span>
                <Select
                  value={formData.location}
                  onValueChange={(value) => handleFormChange("location", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessDetails.data?.locations?.map((loc) => (
                      <SelectItem value={loc.name} key={loc.name}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-medium">Business Unit</span>
              <Select
                value={formData.sbu}
                onValueChange={(value) => handleFormChange("sbu", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Business Unit" />
                </SelectTrigger>
                <SelectContent style={{ maxHeight: "80vh", height: "20rem" }}>
                  {businessDetails.data?.businessUnits?.map((unit) => (
                    <SelectItem value={unit.name} key={unit.name}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setUserDialog((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {userDialog.mode === "add" ? "Create User" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>

      <DialogX
        open={deleteConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteConfirm((prev) => ({ ...prev, open: false }))
        }
      >
        <DialogContentX>
          <DialogHeader>
            <DialogTitleX>Confirm User Deletion</DialogTitleX>
            <DialogDescription>
              Are you sure you want to remove this user from the system? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteConfirm((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleteConfirm.loading}
            >
              {deleteConfirm.loading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}{" "}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>


    </Card>
  );
}
